import { useFormik } from "formik";
import styled from "styled-components";
import { borderWidth, colors, radius, shadows, spacings } from "@/assets/themes";
import Block from "@/components/block";
import { Body14 } from "@/components/text";
import Button from "@/components/button";
import FormControl from "@/components/form-control";
import Icon from "@/components/icon";
import Select from "@/components/select";
import { BUTTON } from "@/components/styles/variants/button";
import { INPUT } from "@/components/styles/variants/input";
import useBreakpoints from "@/modules/hooks/use-breakpoints";
import { JOBBER_SEARCH_ROLES } from "@/utils/index";
import polyglot from "@/utils/polyglot";
import SelectZipCode from "../components/address-modal/select-zip-code";

const StyledZipCode = styled(SelectZipCode)`
  [data-content="input"] {
    padding-top: calc(${spacings.ml} - ${spacings.xs});
    background-color: ${colors.background};
    input {
      padding-right: 0;
    }
  }
`;

const StyledSelect = styled(Select)`
  [data-content="input"] {
    padding-top: calc(${spacings.ml} - ${spacings.xs});
    background-color: ${colors.background};
    input {
      padding-right: 0;
    }
  }
`;

const StyledFormControl = styled(FormControl)`
  margin: 0 !important;
  position: relative;
  flex: 1;
  > label {
    margin: 0 !important;
    position: absolute;
    top: ${spacings.xs};
    z-index: 9;
    left: ${spacings.sm};
  }
`;

const StyledLabel = styled(Body14)`
  margin-bottom: 0;
  margin-top: 0;
`;

const JobbersSeoSearchBar = ({ initialValues, onSubmit, submitOnChange }) => {
  const breakpoints = useBreakpoints();
  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    onSubmit: (values, form) => {
      const newArr = { ...values };
      if (!newArr.city_id) {
        // force submit with null
        newArr.city_id = null;
      }
      delete newArr.city;
      onSubmit(newArr, form);
    },
  });

  const handleZipChange = (values) => {
    formik.setFieldValue("city_id", values.value);
    formik.setFieldValue("city", values.label);
    if (submitOnChange) formik.submitForm();
  };

  const handleRoleChange = ({ value }) => {
    formik.setFieldValue("role", value);
    if (submitOnChange) formik.submitForm();
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <Block
        alignItems={{ xs: "stretch", sm: "center" }}
        backgroundColor={colors.background}
        border={`solid ${borderWidth.m} ${colors.border}`}
        borderRadius={{ xs: radius.l, sm: radius.circle }}
        boxShadow={shadows.xs}
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        gap={spacings.s}
        paddingLeft={{ xs: spacings.s, md: spacings.m }}
        paddingRight={spacings.s}
        paddingY={{ xs: spacings.s, sm: spacings.xs }}
      >
        <StyledFormControl
          label={() => (
            <StyledLabel numberOfLines={1} strong>
              {polyglot.t("jobbers_seo.role_label")}
            </StyledLabel>
          )}
        >
          <StyledSelect
            name="role"
            noPortal={false}
            onChange={handleRoleChange}
            options={JOBBER_SEARCH_ROLES.map((value) => ({
              label: polyglot.t(`roles.${value}`, { count: 0 }),
              value,
            }))}
            size={INPUT.SIZE.SMALL}
            value={formik.values.role}
          />
        </StyledFormControl>
        <StyledFormControl
          label={() => (
            <StyledLabel numberOfLines={1} strong>
              {polyglot.t("search.where")}
            </StyledLabel>
          )}
        >
          <StyledZipCode
            noPortal={false}
            onChange={handleZipChange}
            size={INPUT.SIZE.SMALL}
            value={formik.values.city_id ? { label: formik.values.city, value: null } : {}}
          />
        </StyledFormControl>
        {breakpoints.get({
          xs: (
            <Button.Medium block key="sumbit-responsive-btn-role" type="submit">
              {polyglot.t("common.search")}
            </Button.Medium>
          ),
          sm: (
            <Button.Medium key="sumbit-desktop-btn-role" shape={BUTTON.SHAPE.CIRCLE} type="submit">
              <Icon.Medium name="search" />
            </Button.Medium>
          ),
        })}
      </Block>
    </form>
  );
};

export default JobbersSeoSearchBar;
